import { type ReactNode } from 'react';
import { Monitor, WifiOff, AlertTriangle, Search, Grid3x3, Grid2x2, RefreshCw, Wifi } from 'lucide-react';
import { GRID_COLS, type FilterMode, type GridSize } from './liveScreenUtils';

export interface LiveScreensSummary {
  total: number;
  online: number;
  flagged: number;
}

interface LiveScreensLayoutProps {
  dm: boolean;
  summary: LiveScreensSummary;
  filter: FilterMode;
  setFilter: (f: FilterMode) => void;
  search: string;
  setSearch: (s: string) => void;
  gridSize: GridSize;
  setGridSize: (g: GridSize) => void;
  lastRefresh: Date;
  refreshing?: boolean;
  onRefresh: () => void;
  loading: boolean;
  isEmpty: boolean;
  /** Number of tiles after filtering */
  filteredCount: number;
  children: ReactNode;
}

export function LiveScreensLayout({
  dm, summary, filter, setFilter, search, setSearch,
  gridSize, setGridSize, lastRefresh, refreshing, onRefresh,
  loading, isEmpty, filteredCount, children,
}: LiveScreensLayoutProps) {
  const titleCls = dm ? 'text-slate-100' : 'text-slate-800';
  const descCls = dm ? 'text-slate-400' : 'text-slate-500';
  const mutedCls = dm ? 'text-slate-500' : 'text-slate-400';
  const inputCls = dm
    ? 'bg-slate-800 border-slate-700 text-slate-200 placeholder-slate-500'
    : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400';

  return (
    <div className="space-y-4">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className={`text-lg font-semibold ${titleCls}`}>Live Screens</h2>
          <p className={`text-xs ${descCls}`}>
            Last updated {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · auto-refreshes every 15s
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="btn-glass btn-glass-pill flex items-center gap-1.5 text-sm"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* ── Filter pills ───────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap" data-dm={dm}>
        {([
          { key: 'all'     as const, label: `All (${summary.total})` },
          { key: 'online'  as const, label: `Online (${summary.online})` },
          { key: 'flagged' as const, label: `Flagged (${summary.flagged})` },
        ]).map(({ key, label }) => (
          <button key={key} onClick={() => setFilter(key)}
            className={`btn-glass btn-glass-pill text-xs ${filter === key ? 'btn-glass-blue' : ''}`}>
            {label}
          </button>
        ))}

        {/* Status indicators */}
        <div className="ml-auto flex items-center gap-3 text-xs">
          <span className={`flex items-center gap-1 ${dm ? 'text-emerald-400' : 'text-emerald-600'}`}>
            <Wifi className="h-3.5 w-3.5" /> {summary.online} online
          </span>
          <span className={`flex items-center gap-1 ${mutedCls}`}>
            <WifiOff className="h-3.5 w-3.5" /> {summary.total - summary.online} offline
          </span>
        </div>
      </div>

      {/* ── Search + grid controls ─────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 ${descCls}`} />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search students…"
            className={`w-full h-9 pl-8 pr-3 text-sm rounded-lg border outline-none ${inputCls}`}
          />
        </div>
        <div className="flex items-center gap-1 shrink-0" data-dm={dm}>
          {([
            { key: 'large'  as const, icon: Grid2x2 },
            { key: 'medium' as const, icon: Grid3x3 },
            { key: 'small'  as const, icon: Monitor },
          ]).map(({ key, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setGridSize(key)}
              className={`btn-glass btn-glass-pill p-1.5 ${gridSize === key ? 'btn-glass-blue' : ''}`}
            >
              <Icon className="h-4 w-4" />
            </button>
          ))}
        </div>
      </div>

      {/* ── Grid / empty / loading ─────────────────────────── */}
      {loading ? (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mb-4" />
          <p className={`text-sm ${descCls}`}>Loading student screens…</p>
        </div>
      ) : isEmpty ? (
        <EmptyState filter={filter} dm={dm} noStudents={summary.total === 0} />
      ) : (
        <>
          <div className={`grid gap-3 ${GRID_COLS[gridSize]}`}>
            {children}
          </div>

          {/* Status bar */}
          <div className={`mt-4 flex items-center justify-between text-xs ${mutedCls}`}>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <div className="h-2 w-2 rounded-full bg-emerald-500" />
                <span>Online</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className={`h-2 w-2 rounded-full ${dm ? 'bg-slate-600' : 'bg-slate-300'}`} />
                <span>Offline</span>
              </div>
            </div>
            <span>
              Updated {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              {' · '}{filteredCount} student{filteredCount !== 1 ? 's' : ''}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function EmptyState({ filter, dm, noStudents }: { filter: FilterMode; dm: boolean; noStudents: boolean }) {
  const cls = dm ? 'text-slate-500' : 'text-slate-400';
  if (noStudents) {
    return (
      <div className={`flex flex-col items-center justify-center h-64 ${cls}`}>
        <Monitor className="h-12 w-12 mb-3 opacity-40" />
        <p className="text-base font-medium">No students monitored</p>
      </div>
    );
  }
  if (filter === 'online') {
    return (
      <div className={`flex flex-col items-center justify-center h-64 ${cls}`}>
        <WifiOff className="h-12 w-12 mb-3 opacity-40" />
        <p className="text-base font-medium">No students online right now</p>
        <p className="text-sm mt-1 opacity-70">Students appear here when their browser extension is active</p>
      </div>
    );
  }
  if (filter === 'flagged') {
    return (
      <div className={`flex flex-col items-center justify-center h-64 ${cls}`}>
        <AlertTriangle className="h-12 w-12 mb-3 opacity-40" />
        <p className="text-base font-medium">No flagged activity</p>
      </div>
    );
  }
  return (
    <div className={`flex flex-col items-center justify-center h-64 ${cls}`}>
      <Monitor className="h-12 w-12 mb-3 opacity-40" />
      <p className="text-base font-medium">No students match your filter</p>
    </div>
  );
}
